// React
import { useState } from "react";

// Store
import useCoachStore from "../../store/coachStore.js";

// Hooks
import { useAppTheme } from "../../hooks/useAppTheme.js";

// Components
import { 
    Avatar, Box, Button, Divider, FormControlLabel, Grid, IconButton, 
    InputAdornment, InputLabel, Modal, Paper, Radio, 
    RadioGroup, Stack, TextField, Typography, LinearProgress 
} from "@mui/material";
import { 
    CloseOutlined, PersonAddOutlined, EmailOutlined, EmojiEventsOutlined, 
    FeedbackOutlined
} from '@mui/icons-material';
import CustomFormControl from "../custom/FormControl.js";
import { CustomSelect, CustomMenuItem, getMenuProps } from "../custom/Select.js";
import SearchableAthleteSelector from "../custom/SearchableAthleteSelector.js";
import CustomSnackbar from '../custom/CustomSnackbar.js';

// Style
import { blue, green, red, orange } from "@mui/material/colors/index.js";
import { darkTheme } from "../../styles/colorSchemes/index.js";

// Other
import { addAthlete } from '../../service/httpService/athlete/index.js';
import { sendInvitationLink } from '../../service/httpService/invitationLink/index.js';
import { addCoach } from '../../service/httpService/coach/index.js';
import { addParent } from '../../service/httpService/parent/index.js';

const AddUser = (props) => {
    const { athletes, onClose, open, profileType, setLinkMessage, onRefreshUserLists } = props;
    const { setOpenLink } = useCoachStore();
    const { isDarkMode } = useAppTheme();
    
    const [email, setEmail] = useState("");
    const [squad, setSquad] = useState("");
    const [selectedAthletes, setSelectedAthletes] = useState([]);
    const [hasExtendedFeedback, setHasExtendedFeedback] = useState("");
    const [openFail, setOpenFail] = useState(false);
    const [failMessage, setFailMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [squadSelectAnchorEl, setSquadSelectAnchorEl] = useState(null);

    const primaryColor = isDarkMode ? orange[500] : blue[600];

    const resetForm = () => {
        setEmail("");
        setSquad("");
        setSelectedAthletes([]);
        setHasExtendedFeedback("");
        setLoading(false);
    };

    const handleClose = () => {
        onClose();
        setOpenFail(false);
        resetForm();
    };

    const onChangeSquad = (event) => {
        setSquad(event.target.value);
    };

    const onChangeSelectedAthletes = (event) => {
        setSelectedAthletes(event.target.value);
    };

    const onChangeHasExtendedFeedback = (event) => {
        setHasExtendedFeedback(event.target.value);
    };

    const onChangeEmail = (event) => {
        setEmail(String(event.target.value).toLocaleLowerCase());
    };

    const validate = (email) => {
        if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
            if (profileType === "athlete") {
                if (squad !== "" && hasExtendedFeedback !== "") {
                    return true;
                }
            } else {
                return true;
            }
        }
        return false;
    };

    const addUser = async () => {
        setLoading(true);
        if(profileType === 'athlete') {
            if (!validate(email)) {
                setOpenFail(true);
                setFailMessage("Bitte füllen Sie alle Felder korrekt aus!");
                setLoading(false);
                return;
            }

            const resAthlete = await addAthlete({
                email: email,
                squad: squad,
                hasExtendedFeedback: hasExtendedFeedback === 'true' || hasExtendedFeedback === true
            }).catch((error) => {
                if (error.response && error.response.status === 404) {
                    return null;
                }
                throw error;
            });

            if(!resAthlete) {
                setOpenFail(true);
                setFailMessage("Ein Profil mit dieser E-Mail-Adresse existiert bereits!");
                setLoading(false);
                return;
            }

            const resInvLink = await sendInvitationLink(email).catch(() => null);
            
            if(!resInvLink) {
                setOpenFail(true);
                setFailMessage("Erstellen des Einladungslinks fehlgeschlagen!");
                setLoading(false);
                return;
            }

            if (onRefreshUserLists) {
                onRefreshUserLists();
            }

            handleClose();
            /*const link = resInvLink.data.invitationLink;
            const baseUrl = process.env.NODE_ENV === 'production' 
                ? 'https://s-i-mon.de' 
                : 'http://localhost:3001';*/
            setLinkMessage(`Die Einladungs-E-Mail wurde erfolgreich versendet.`);
            setOpenLink(true);
        }

        if (profileType === "coach" || profileType === "parent") {
            if (!validate(email)) {
                setOpenFail(true);
                setFailMessage("Ungültige E-Mail-Adresse!");
                setLoading(false);
                return;
            }
            
            if (!selectedAthletes.length) {
                setOpenFail(true);
                setFailMessage("Bitte wählen Sie mindestens einen Athleten aus!");
                setLoading(false);
                return;
            }
        
            const action = profileType === "coach" ? addCoach : addParent;
            
            action({email: email, athletes: selectedAthletes})
                .then(() => {
                    sendInvitationLink(email)
                        .then(response => {
                            setOpenLink(true);
                            handleClose();
                            /*const link = response.data.invitationLink;
                            const baseUrl = process.env.NODE_ENV === 'production' 
                                ? 'https://s-i-mon.de' 
                                : 'http://localhost:3001';*/
                            setLinkMessage(`Die Einladungs-E-Mail wurde erfolgreich versendet.`);
                            
                            if (onRefreshUserLists) {
                                onRefreshUserLists();
                            }
                        })
                        .catch(() => {
                            setOpenFail(true);
                            setFailMessage("Erstellen des Einladungslinks fehlgeschlagen!");
                            setLoading(false);
                        });
                })
                .catch(() => {
                    setOpenFail(true);
                    setFailMessage(`Hinzufügen des ${profileType === "coach" ? "Trainers" : "Elternteils"} fehlgeschlagen!`);
                    setLoading(false);
                });
        }
    };

    return (
        <Modal 
            open={open}
            onClose={handleClose}
            sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                p: 2,
                '@media (max-width: 900px)': {
                    alignItems: 'flex-start',
                    p: 0,
                    overflow: 'auto'
                }
            }}
        >
            <Paper 
                sx={{ 
                    width: '90%',
                    maxWidth: '1000px',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    borderRadius: 3,
                    boxShadow: isDarkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    bgcolor: isDarkMode ? darkTheme.accordionBg : undefined,
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    position: 'relative',
                    '@media (max-width: 900px)': {
                        width: '100%',
                        minHeight: '100vh',
                        maxHeight: '100vh',
                        borderRadius: 0,
                        m: 0
                    }
                }}
            >
                <CustomSnackbar 
                    open={openFail} 
                    onClose={() => setOpenFail(false)} 
                    message={failMessage} 
                    severity="error" 
                    autoHideDuration={4000}
                />

                {/* Close Button */}
                <IconButton 
                    onClick={handleClose}
                    sx={{ 
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 10,
                        bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(255, 255, 255, 0.9)',
                        color: isDarkMode ? darkTheme.textSecondary : undefined,
                        '&:hover': {
                            bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
                            transform: 'rotate(90deg)',
                            transition: 'transform 0.3s ease'
                        }
                    }}
                >
                    <CloseOutlined />
                </IconButton>

                {/* Left Side - Info */}
                <Box 
                    sx={{ 
                        width: { xs: '100%', md: '35%' },
                        background: `linear-gradient(135deg, ${primaryColor}22 0%, ${primaryColor}55 100%)`,
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 3,
                        minHeight: { xs: '300px', md: 'auto' },
                        '@media (max-width: 900px)': {
                            p: 3,
                            gap: 2,
                            minHeight: 'auto',
                            flexShrink: 0,
                            alignItems: 'center',
                            textAlign: 'center'
                        }
                    }}
                >
                    <Avatar 
                        sx={{ 
                            width: 80, 
                            height: 80, 
                            bgcolor: primaryColor,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            '@media (max-width: 900px)': {
                                mr: 5,
                                width: 64,
                                height: 64
                            }
                        }}
                    >
                        <PersonAddOutlined sx={{ fontSize: 40 }} />
                    </Avatar>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography 
                            variant="h5" 
                            fontWeight={600} 
                            color={isDarkMode ? darkTheme.textPrimary : 'text.primary'}
                            sx={{
                                '@media (max-width: 900px)': {
                                    fontSize: '1.25rem',
                                    mr: 5
                                }
                            }}
                        >
                            {profileType === "athlete" && "Athlet hinzufügen"}
                            {profileType === "coach" && "Trainer hinzufügen"}
                            {profileType === "parent" && "Elternteil hinzufügen"}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'}
                            sx={{ 
                                mt: 1,
                                '@media (max-width: 900px)': {
                                    fontSize: '0.875rem',
                                    mr: 5
                                }
                            }}
                        >
                            {profileType === "athlete" && "Erstellen Sie ein neues Profil für einen Athleten und laden Sie ihn per E-Mail ein."}
                            {profileType === "coach" && "Erstellen Sie ein neues Profil für einen Trainer und weisen Sie ihm Athleten zu."}
                            {profileType === "parent" && "Erstellen Sie ein neues Profil für einen Elternteil und weisen Sie ihm Athleten zu."}
                        </Typography>
                    </Box>
                </Box>

                {/* Right Side - Form */}
                <Box 
                    sx={{ 
                        width: { xs: '100%', md: '65%' },
                        p: 4,
                        overflow: 'auto',
                        maxHeight: { xs: 'calc(90vh - 300px)', md: '90vh' },
                        display: 'flex',
                        flexDirection: 'column',
                        '@media (max-width: 900px)': {
                            maxHeight: '100%',
                            p: 2,
                            flex: 1,
                            pb: 4,
                            boxSizing: 'border-box'
                        }
                    }}
                >
                    <Typography 
                        variant="h5" 
                        fontWeight={600}
                        color={isDarkMode ? darkTheme.textPrimary : undefined}
                        sx={{ 
                            mb: 3, 
                            mt: { xs: 2, md: 0 },
                            '@media (max-width: 900px)': {
                                fontSize: '1.25rem',
                                mb: 2,
                                mt: 1
                            }
                        }}
                    >
                        Daten eingeben
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="E-Mail"
                                variant="outlined"
                                value={email}
                                onChange={onChangeEmail}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        bgcolor: isDarkMode ? darkTheme.headerBg : undefined,
                                        '& fieldset': {
                                            borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                                        },
                                        '&:hover fieldset': {
                                            borderColor: primaryColor,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: primaryColor,
                                        }
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: isDarkMode ? darkTheme.textSecondary : undefined,
                                        '&.Mui-focused': {
                                            color: primaryColor,
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                                    }
                                }}
                            />
                        </Grid>

                        {profileType === "athlete" && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <CustomFormControl sx={{ width: '100%' }} color={primaryColor} isDarkMode={isDarkMode} darkTheme={darkTheme}>
                                        <InputLabel id="squad-label" sx={{ 
                                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                                            '&.Mui-focused': {
                                                color: `${primaryColor} !important`,
                                            }
                                        }}>Kader</InputLabel>
                                        <CustomSelect
                                            ref={setSquadSelectAnchorEl}
                                            labelId="squad-label"
                                            value={squad}
                                            label="Kader"
                                            onChange={onChangeSquad}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <EmojiEventsOutlined sx={{ 
                                                        color: isDarkMode ? darkTheme.textSecondary : 'action.active', 
                                                        ml: 0,
                                                        transition: 'none'
                                                    }} />
                                                </InputAdornment>
                                            }
                                            isDarkMode={isDarkMode}
                                            darkTheme={darkTheme}
                                            primaryColor={primaryColor}
                                            MenuProps={(() => {
                                                const base = getMenuProps(isDarkMode, darkTheme);
                                                return {
                                                    ...base,
                                                    anchorEl: squadSelectAnchorEl,
                                                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                                    transformOrigin: { vertical: 'top', horizontal: 'left' },
                                                    PaperProps: {
                                                        ...base.PaperProps,
                                                        sx: {
                                                            ...base.PaperProps?.sx,
                                                            width: squadSelectAnchorEl ? squadSelectAnchorEl.clientWidth : undefined,
                                                            minWidth: squadSelectAnchorEl ? squadSelectAnchorEl.clientWidth : undefined,
                                                        },
                                                    },
                                                };
                                            })()}
                                        >
                                            <CustomMenuItem value="Damen" isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Damen</CustomMenuItem>
                                            <CustomMenuItem value="Herren" isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>Herren</CustomMenuItem>
                                            <CustomMenuItem value="U19" isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>U19</CustomMenuItem>
                                            <CustomMenuItem value="U17" isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>U17</CustomMenuItem>
                                            <CustomMenuItem value="U15" isDarkMode={isDarkMode} darkTheme={darkTheme} primaryColor={primaryColor}>U15</CustomMenuItem>
                                        </CustomSelect>
                                    </CustomFormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <Box 
                                        sx={{ 
                                            p: 2, 
                                            border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.12)', 
                                            borderRadius: 2,
                                            bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(0, 0, 0, 0.01)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2
                                        }}
                                    >
                                        <FeedbackOutlined sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 24 }} />
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="subtitle2" color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ mb: 0.5 }}>
                                                Erweiterte Rückmeldung
                                            </Typography>
                                            <RadioGroup row value={hasExtendedFeedback} onChange={onChangeHasExtendedFeedback}>
                                                <FormControlLabel 
                                                    value={true} 
                                                    control={<Radio sx={{ color: green[500], '&.Mui-checked': { color: green[500] }}} />} 
                                                    label={<Typography sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>Ja</Typography>}
                                                />
                                                <FormControlLabel 
                                                    value={false} 
                                                    control={<Radio sx={{ color: red[500], '&.Mui-checked': { color: red[500] }}} />} 
                                                    label={<Typography sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>Nein</Typography>}
                                                />
                                            </RadioGroup>
                                        </Box>
                                    </Box>
                                </Grid>
                            </>
                        )}

                        {(profileType === "coach" || profileType === "parent") && (
                            <Grid item xs={12}>
                                <SearchableAthleteSelector
                                    athletes={athletes}
                                    selectedAthletes={selectedAthletes}
                                    onChangeSelectedAthletes={onChangeSelectedAthletes}
                                    label="Athleten"
                                    color={primaryColor}
                                />
                            </Grid>
                        )}
                    </Grid>
                    
                    <Divider sx={{ 
                        mt: 4, 
                        mb: 3,
                        '@media (max-width: 900px)': {
                            mt: 3,
                            mb: 2
                        }
                    }}/>

                    {loading && (
                        <Box sx={{ mb: 2 }}> 
                            <LinearProgress 
                                sx={{ 
                                    height: 6, 
                                    borderRadius: 3,
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: primaryColor
                                    }
                                }} 
                            />
                        </Box>
                    )}

                    <Stack 
                        direction="row" 
                        justifyContent="flex-end"
                        sx={{
                            '@media (max-width: 900px)': {
                                justifyContent: 'stretch',
                                width: '100%'
                            }
                        }}
                    >
                        <Button 
                            onClick={addUser}
                            variant="contained"
                            disabled={loading}
                            sx={{ 
                                bgcolor: primaryColor,
                                textTransform: 'none',
                                px: 4,
                                py: 1.2,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                '&:hover': { 
                                    bgcolor: primaryColor,
                                    filter: 'brightness(0.9)',
                                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)'
                                },
                                '@media (max-width: 900px)': {
                                    width: '100%',
                                    px: 3
                                }
                            }}
                        >
                            Hinzufügen
                        </Button>
                    </Stack>
                </Box>
            </Paper>
        </Modal>
    );
};

export default AddUser;